import { createRoutine } from 'redux-saga-routines'
import {
  fork,
  put,
  take,
  takeLatest,
  select,
  call,
  spawn,
  takeEvery,
  delay
} from '@redux-saga/core/effects'
import { eventChannel } from 'redux-saga'
import { head, pathOr } from 'ramda'
import qs from 'qs'
import { v4 as uuid } from 'uuid'
import MessagesService from 'src/services/MessagesService'
import DictionariesService from 'src/services/DictionariesService'
import LaravelEchoService from 'src/services/LaravelEchoService'
import ExchangeService from 'src/services/ExchangeService'
import ReservationService from 'src/services/ReservationService'
import ChatSyncService from 'src/services/ChatSyncService'
import { API_STATES } from 'src/ducks/consts'
import {
  selectCurrentUser,
  selectCurrentUserCompanyId
} from 'src/features/account/duck/selectors'
import { toggleChatNotifications } from 'src/features/account/duck/actions'
import notificationSound from 'src/assets/intuition.mp3'
import { showChatMessageNotificationToast } from 'src/features/messages/components/ChatMessageNotificationToast'
import {
  acceptExportReservationRoutine,
  acceptImportReservationRoutine,
  acceptGeneralReservationRoutine,
  acceptPostImportReservationRoutine,
  acceptFreeCarrierReservationRoutine
} from 'src/features/reservations/duck/actions'
import {
  isChatMuted,
  selectConversationDetails,
  selectMessagesDetails,
  isConversationOpened,
  selectChatSessionDetailsByChatId,
  selectChatSessionById,
  selectChatSessions,
  selectActiveChatId,
  isMessagesListOpened
} from './selectors'
import {
  normalizeToInterlocutor,
  normalizeMessagesList,
  normalizeSentMessage,
  normalizeLastMessagesList,
  normalizeFoundUsers,
  normalizeContext
} from './normalizers'
import { MESSAGE_EVENT_TYPES, CONTEXT_TYPE_MAP } from './consts'
import {
  ensureChatId,
  hasServerMessageId,
  isUnreadIncomingMessage
} from './helpers'
import { Message } from './records'

const sound =
  typeof window !== 'undefined' ? new Audio(notificationSound) : null

export const setMessagesListOpenedRoutine = createRoutine(
  'SET_MESSAGES_LIST_OPENED'
)
export const setConversationOpenedRoutine = createRoutine(
  'SET_CONVERSATION_OPENED'
)
export const sendMessageRoutine = createRoutine('SEND_MESSAGE')
export const bootstrapConversationRoutine = createRoutine(
  'BOOTSTRAP_CONVERSATION'
)
export const getConversationMessagesRoutine = createRoutine(
  'GET_CONVERSATION_MESSAGES'
)
export const clearConversationRoutine = createRoutine('CLEAR_CONVERSATION')
export const getLastMessagesListRoutine = createRoutine(
  'GET_LAST_MESSAGES_LIST'
)
export const getLastContextualMessagesListRoutine = createRoutine(
  'GET_LAST_CONTEXTUAL_MESSAGES_LIST'
)
export const getLastPersonalMessagesListRoutine = createRoutine(
  'GET_LAST_PERSONAL_MESSAGES_LIST'
)
export const getUnreadMessagesCountRoutine = createRoutine(
  'GET_UNREAD_MESSAGES_COUNT'
)
export const searchForUserConversationsRoutine = createRoutine(
  'SEARCH_FOR_USER_CONVERSATIONS'
)
export const clearLastMessagesListsRoutine = createRoutine(
  'CLEAR_LAST_MESSAGES_LISTS'
)
export const muteChatRoutine = createRoutine('MUTE_CHAT')
export const unmuteChatRoutine = createRoutine('UNMUTE_CHAT')
export const muteConversationRoutine = createRoutine('MUTE_CONVERSATION')
export const unmuteConversationRoutine = createRoutine('UNMUTE_CONVERSATION')
export const toggleConversationNotificationsRoutine = createRoutine(
  'TOGGLE_CONVERSATION_NOTIFICATIONS'
)
export const getConversationContextRoutine = createRoutine(
  'GET_CONVERSATION_CONTEXT'
)
export const markConversationAsSeenRoutine = createRoutine(
  'MARK_CONVERSATION_AS_SEEN'
)
export const markMessageAsSeenRoutine = createRoutine('MARK_MESSAGE_AS_SEEN')
export const reserveOfferRoutine = createRoutine('RESERVE_OFFER')
export const acceptReservationRoutine = createRoutine('ACCEPT_RESERVATION')
export const rejectReservationRoutine = createRoutine('REJECT_RESERVATION')
export const cancelReservationRoutine = createRoutine('CANCEL_RESERVATION')
export const getContextAvailableActionsRoutine = createRoutine(
  'GET_CONTEXT_AVAILABLE_ACTIONS'
)
export const MESSAGE_EVENT_USER_MESSAGE_SENT = 'MESSAGE_EVENT_USER_MESSAGE_SENT'
export const MESSAGE_EVENT_SYSTEM_MESSAGE_SENT =
  'MESSAGE_EVENT_SYSTEM_MESSAGE_SENT'
export const CHAT_PRESENCE_CHANNEL_INIT = 'CHAT_PRESENCE_CHANNEL_INIT'
export const CHAT_PRESENCE_CHANNEL_JOIN = 'CHAT_PRESENCE_CHANNEL_JOIN'
export const CHAT_PRESENCE_CHANNEL_LEAVE = 'CHAT_PRESENCE_CHANNEL_LEAVE'
export const CHAT_OTHER_TAB_MESSAGE_SENT = 'CHAT_OTHER_TAB_MESSAGE_SENT'
export const CHAT_CONVERSATION_MESSAGES_READ = 'CHAT_CONVERSATION_MESSAGES_READ'
export const setChatSessionActiveRoutine = createRoutine(
  'SET_CHAT_SESSION_ACTIVE'
)
export const setChatSessionMinimizedRoutine = createRoutine(
  'SET_CHAT_SESSION_MINIMIZED'
)
export const closeChatSessionRoutine = createRoutine('CLOSE_CHAT_SESSION')
export const CHAT_SESSION_UI_UPDATED = 'CHAT_SESSION_UI_UPDATED'
export const updateChatSessionUi = payload => ({
  type: CHAT_SESSION_UI_UPDATED,
  payload
})
export const setChatDockConstraintsRoutine = createRoutine(
  'SET_CHAT_DOCK_CONSTRAINTS'
)

//actions

function* setMessagesListOpened({ payload }) {
  yield put(setMessagesListOpenedRoutine.success(payload))
}

function* setConversationOpened({ payload }) {
  const conversationChatMap = yield select(
    state => state.messages.conversationChatMap
  )
  const chatSessions = yield select(selectChatSessions)
  const resolvedChatIdFromMap =
    payload?.conversationId && conversationChatMap
      ? conversationChatMap[String(payload.conversationId)]
      : null
  const normalizedContextId =
    payload?.contextId === undefined ? null : payload.contextId
  const normalizedContextType =
    payload?.contextType === undefined ? null : payload.contextType
  const resolvedChatIdFromSession = Object.entries(chatSessions || {}).find(
    ([, session]) => {
      if (
        payload?.conversationId &&
        String(session?.conversationId) === String(payload.conversationId)
      ) {
        return true
      }

      return (
        payload?.receiverId != null &&
        String(session?.receiverId) === String(payload.receiverId) &&
        String(session?.contextId ?? null) === String(normalizedContextId) &&
        String(session?.contextType ?? null) === String(normalizedContextType)
      )
    }
  )?.[0]

  const chatId =
    resolvedChatIdFromMap || resolvedChatIdFromSession || ensureChatId(payload)
  const existingSession = yield select(state =>
    selectChatSessionById(state, chatId)
  )

  const normalizedPayload = {
    ...({
      receiverId: existingSession?.receiverId,
      contextId: existingSession?.contextId,
      contextType: existingSession?.contextType,
      conversationId: existingSession?.conversationId
    } || {}),
    ...payload,
    chatId,
    conversationOpened: true
  }

  yield put(setConversationOpenedRoutine.success(normalizedPayload))
}

function* setChatSessionActive({ payload }) {
  const chatId = ensureChatId(payload)
  yield put(
    setChatSessionActiveRoutine.success({
      ...payload,
      chatId
    })
  )
}

function* setChatSessionMinimized({ payload }) {
  const chatId = ensureChatId(payload)
  yield put(
    setChatSessionMinimizedRoutine.success({
      ...payload,
      chatId
    })
  )
}

function* closeChatSession({ payload }) {
  const chatId = ensureChatId(payload)
  yield put(closeChatSessionRoutine.success({ ...payload, chatId }))
}

function* setChatDockConstraints({ payload }) {
  yield put(setChatDockConstraintsRoutine.success(payload))
}

function* sendMessage({ payload }) {
  const { callback, onError, message, chatId } = payload
  const requestedChatId = yield select(
    state =>
      state.messages.conversationChatMap?.[payload.conversationId] || chatId
  )
  const conversationDetailsRecord = yield select(state =>
    requestedChatId
      ? selectChatSessionDetailsByChatId(state, { chatId: requestedChatId })
      : selectConversationDetails(state)
  )
  const conversationDetails =
    conversationDetailsRecord &&
    typeof conversationDetailsRecord.toJS === 'function'
      ? conversationDetailsRecord.toJS()
      : { ...(conversationDetailsRecord || {}) }
  const resolvedChatId = requestedChatId || conversationDetails.chatId
  const existingMessage = (conversationDetails.messages || [])
    .reduce((all, group) => all.concat(group), [])
    .find(item => item.clientId === payload.clientId)
  if (payload.clientId && existingMessage?.sendStatus !== 'failed') return
  if (!message?.trim()) return
  const clientId = payload.clientId || uuid()
  const currentUser = yield select(selectCurrentUser)
  const pendingMessage = new Message({
    id: `client:${clientId}`,
    clientId,
    text: message,
    createdAt: existingMessage?.createdAt || new Date().toISOString(),
    sendStatus: 'pending',
    knownMessageIds:
      existingMessage?.knownMessageIds ||
      (conversationDetails.messages || [])
        .reduce((all, group) => all.concat(group), [])
        .filter(hasServerMessageId)
        .map(item => String(item.id)),
    knownMessageDate:
      existingMessage?.knownMessageDate ||
      (conversationDetails.messages || [])
        .reduce((all, group) => all.concat(group), [])
        .filter(hasServerMessageId)
        .slice(-1)[0]?.createdAt ||
      null
  })
  yield put(
    sendMessageRoutine.request({
      chatId: resolvedChatId,
      message: pendingMessage,
      retry: Boolean(payload.clientId)
    })
  )
  const resolvedReceiverId =
    payload?.receiverId ??
    conversationDetails.receiverId ??
    conversationDetails.interlocutor?.id ??
    null
  const requestPayload = Object.entries({
    conversationId:
      payload.conversationId ?? conversationDetails.conversationId ?? null,
    receiverId: resolvedReceiverId,
    contextId: payload.contextId ?? conversationDetails.contextId ?? null,
    contextType: payload.contextType ?? conversationDetails.contextType ?? null,
    message
  }).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value
    }
    return acc
  }, {})

  if (!requestPayload.conversationId && !requestPayload.receiverId) {
    yield put(sendMessageRoutine.failure({ chatId: resolvedChatId, clientId }))
    console.warn('Missing receiverId for chat message', {
      chatId: resolvedChatId
    })
    if (typeof onError === 'function') {
      try {
        onError(new Error('Missing receiverId for chat message'))
      } catch (callbackError) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to execute onError callback', callbackError)
        }
      }
    }
    return
  }

  let data
  let normalizedMessage
  try {
    const response = yield call(MessagesService.sendMessage, requestPayload)
    data = response.data

    normalizedMessage = normalizeSentMessage(pathOr('', ['message'], data))

    // Keep the local identity even when the API only returns text. A later
    // history fetch or socket event replaces this bubble with its server ID.
    const currentSession = yield select(
      state =>
        state.messages.chatSessions?.[
          state.messages.conversationChatMap?.[data.conversationId] ||
            resolvedChatId
        ]
    )
    const currentMessage = (currentSession?.messages || [])
      .reduce((all, group) => all.concat(group), [])
      .find(item => item.clientId === clientId)
    normalizedMessage = hasServerMessageId(normalizedMessage)
      ? normalizedMessage.merge({ clientId, sendStatus: 'sent' })
      : (currentMessage || pendingMessage).merge({
          text: normalizedMessage.text || message,
          seenAt: normalizedMessage.seenAt || currentMessage?.seenAt || null,
          sendStatus: 'sent'
        })

    yield put(
      sendMessageRoutine.success({
        chatId: resolvedChatId,
        clientId,
        data,
        message: normalizedMessage
      })
    )
    if (!hasServerMessageId(normalizedMessage) && data.conversationId) {
      yield put(
        getConversationMessagesRoutine.trigger({
          chatId: resolvedChatId,
          conversationId: data.conversationId,
          refresh: true,
          values: { limit: 10 }
        })
      )
    }
  } catch (e) {
    yield put(sendMessageRoutine.failure({ chatId: resolvedChatId, clientId }))
    console.log(e)
    if (typeof onError === 'function') {
      try {
        onError(e)
      } catch (callbackError) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to execute onError callback', callbackError)
        }
      }
    }
    return
  }
  // A notification or scroll callback failing must not turn a sent message
  // into a failed one and invite the user to send it twice.
  try {
    ChatSyncService.getInstance().broadcastMessageSent({
      userId: currentUser.id,
      conversationId: data.conversationId,
      receiverId: resolvedReceiverId,
      contextId: requestPayload.contextId,
      contextType: requestPayload.contextType,
      message: normalizedMessage.toJS()
    })
    if (typeof callback === 'function') callback()
  } catch (e) {
    console.log(e)
  }
}

function* bootstrapConversation({ payload = {} }) {
  const { chatId } = payload
  const conversationDetailsRecord = yield select(state =>
    chatId
      ? selectChatSessionDetailsByChatId(state, { chatId })
      : selectConversationDetails(state)
  )
  const conversationDetails =
    conversationDetailsRecord &&
    typeof conversationDetailsRecord.toJS === 'function'
      ? conversationDetailsRecord.toJS()
      : { ...(conversationDetailsRecord || {}) }

  const normalizedDetails = {
    chatId: chatId || conversationDetails.chatId,
    conversationId:
      payload?.conversationId ?? conversationDetails.conversationId ?? null,
    receiverId: payload?.receiverId ?? conversationDetails.receiverId ?? null,
    contextId: payload?.contextId ?? conversationDetails.contextId ?? null,
    contextType: payload?.contextType ?? conversationDetails.contextType ?? null
  }

  const requestPayload = Object.entries({
    conversationId: normalizedDetails.conversationId,
    receiverId: normalizedDetails.receiverId,
    contextId: normalizedDetails.contextId,
    contextType: normalizedDetails.contextType
  }).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value
    }
    return acc
  }, {})

  const currentUser = yield select(selectCurrentUser)

  try {
    let data = null
    const searchResult = yield call(
      MessagesService.findConversation,
      requestPayload
    )
    data = searchResult.data
    if (!data.id) {
      const startResult = yield call(
        MessagesService.startConversation,
        requestPayload
      )
      data = startResult.data
    }
    const interlocutor = head(
      pathOr([], ['participants'], data).filter(v => v.id !== currentUser.id)
    )
    const loggedInUser = head(
      pathOr([], ['participants'], data).filter(v => v.id === currentUser.id)
    )
    const mappedChatId = yield select(
      state => state.messages.conversationChatMap?.[String(data.id)]
    )
    const matchingChatId = yield select(
      state =>
        Object.entries(state.messages.chatSessions || {}).find(
          ([candidateChatId, session]) =>
            String(candidateChatId) !== String(normalizedDetails.chatId) &&
            String(session?.conversationId) === String(data.id)
        )?.[0]
    )
    const resolvedChatId =
      mappedChatId || matchingChatId || normalizedDetails.chatId

    yield put(
      bootstrapConversationRoutine.success({
        chatId: resolvedChatId,
        previousChatId:
          String(resolvedChatId) !== String(normalizedDetails.chatId)
            ? normalizedDetails.chatId
            : null,
        interlocutor: normalizeToInterlocutor(interlocutor || {}),
        conversationId: data.id,
        isConversationMuted: pathOr(false, ['pivot', 'isMuted'], loggedInUser),
        isAvailable: data.isAvailable,
        receiverId: normalizedDetails.receiverId ?? interlocutor?.id ?? null,
        contextId: normalizedDetails.contextId ?? data.contextId ?? null,
        contextType: normalizedDetails.contextType ?? data.contextType ?? null
      })
    )
    if (data.contextId) {
      yield call(getConversationContext, {
        payload: {
          contextType: data.contextType,
          contextId: data.contextId,
          companyName: pathOr('', ['company', 'name'], interlocutor)
        },
        meta: { chatId: resolvedChatId }
      })
    }

    yield call(getConversationMessages, {
      payload: {
        chatId: resolvedChatId,
        conversationId: data.id,
        values: { limit: 10 }
      }
    })
  } catch (e) {
    //conversation not found

    let interlocutor = null
    if (normalizedDetails.receiverId) {
      try {
        const { data } = yield call(
          DictionariesService.users,
          `filter[name]=${normalizedDetails.receiverId}`
        )
        interlocutor = head(data)
      } catch (fallbackError) {
        console.log(fallbackError)
        return
      }
    }

    yield put(
      bootstrapConversationRoutine.success({
        chatId: normalizedDetails.chatId,
        interlocutor: normalizeToInterlocutor(interlocutor || {}),
        conversationId: null,
        receiverId: normalizedDetails.receiverId ?? interlocutor?.id ?? null,
        contextId: normalizedDetails.contextId,
        contextType: normalizedDetails.contextType
      })
    )

    if (normalizedDetails.contextId) {
      yield call(getConversationContext, {
        payload: {
          contextType: normalizedDetails.contextType,
          contextId: normalizedDetails.contextId,
          companyName: pathOr('', ['company', 'name'], interlocutor)
        },
        meta: { chatId: normalizedDetails.chatId }
      })
    }
  }
}

function* getConversationContext({ payload, meta }) {
  yield put(getConversationContextRoutine.request({ chatId: meta?.chatId }))
  try {
    const { data } = yield call(MessagesService.getConversationContext, {
      stock: CONTEXT_TYPE_MAP[payload.contextType],
      offerId: payload.contextId
    })

    const currentUserCompanyId = yield select(selectCurrentUserCompanyId)
    yield put(
      getConversationContextRoutine.success({
        chatId: meta?.chatId,
        data: normalizeContext(payload.contextType, {
          ...data,
          companyName: payload.companyName,
          currentUserCompanyId,
          contextId: payload.contextId
        })
      })
    )
  } catch (e) {
    console.log(e)
    yield put(getConversationContextRoutine.failure({ chatId: meta?.chatId }))
  }
}

const conversationRefreshes = new Map()

function* getConversationMessages({ payload }) {
  const currentUser = yield select(selectCurrentUser)
  const chatId = payload?.chatId
  const conversationDetails = yield select(state =>
    chatId
      ? selectChatSessionDetailsByChatId(state, { chatId })
      : selectConversationDetails(state)
  )
  if (chatId && !conversationDetails?.chatId) return
  if (
    payload.refresh &&
    conversationDetails?.messagesState === API_STATES.IN_PROGRESS
  ) {
    return
  }
  const conversationId =
    payload?.conversationId ||
    (conversationDetails && typeof conversationDetails.get === 'function'
      ? conversationDetails.get('conversationId')
      : conversationDetails?.conversationId)
  const normalizedChatId =
    chatId ||
    (conversationDetails && typeof conversationDetails.get === 'function'
      ? conversationDetails.get('chatId')
      : conversationDetails?.chatId)

  const refreshKey = payload.refresh
    ? `${currentUser.id}|${conversationId}`
    : null
  if (refreshKey && conversationRefreshes.has(refreshKey)) {
    conversationRefreshes.get(refreshKey).repeat = true
    return
  }
  if (refreshKey) conversationRefreshes.set(refreshKey, { repeat: false })

  yield put(
    getConversationMessagesRoutine.request({
      chatId: normalizedChatId,
      conversationId,
      refresh: Boolean(payload.refresh)
    })
  )
  try {
    const { data } = yield call(
      MessagesService.getConversationMessages,
      conversationId,
      payload.values
    )
    const normalized = normalizeMessagesList(data, currentUser.id)
    yield put(
      getConversationMessagesRoutine.success({
        data: normalized,
        conversationId,
        chatId: normalizedChatId,
        refresh: Boolean(payload.refresh)
      })
    )
  } catch (e) {
    console.log(e)
    yield put(
      getConversationMessagesRoutine.failure({
        chatId: normalizedChatId,
        conversationId,
        refresh: Boolean(payload.refresh)
      })
    )
  } finally {
    if (refreshKey) {
      const repeat = conversationRefreshes.get(refreshKey)?.repeat
      conversationRefreshes.delete(refreshKey)
      if (repeat) yield put(getConversationMessagesRoutine.trigger(payload))
    }
  }
}

function* markConversationAsSeen({ payload }) {
  // Only explicitly visible messages may be acknowledged. Opening a window
  // does not mean that its entire history has been read.
  for (const messageId of payload?.messageIds || []) {
    yield call(markMessageAsSeen, { payload: { ...payload, messageId } })
  }
}

const pendingReadRequests = new Set()

function* markMessageAsSeen({ payload }) {
  let requestKey
  let ownsRequest = false
  try {
    if (!payload?.messageId) return
    if (
      typeof document === 'undefined' ||
      document.hidden ||
      !document.hasFocus()
    ) {
      return
    }

    const conversationDetails = yield select(state =>
      payload?.chatId
        ? selectChatSessionDetailsByChatId(state, { chatId: payload.chatId })
        : selectConversationDetails(state)
    )

    const conversationId = conversationDetails?.conversationId
    const activeChatId = yield select(selectActiveChatId)
    if (String(conversationDetails?.chatId) !== String(activeChatId)) return
    const message = (conversationDetails?.messages || [])
      .reduce((all, group) => all.concat(group), [])
      .find(item => String(item.id) === String(payload.messageId))
    if (
      !conversationId ||
      !hasServerMessageId(message) ||
      !isUnreadIncomingMessage(message) ||
      conversationDetails.readReceipts?.[String(payload.messageId)]
    ) {
      return
    }
    const currentUser = yield select(selectCurrentUser)
    requestKey = `${currentUser.id}|${conversationId}|${payload.messageId}`
    if (pendingReadRequests.has(requestKey)) return
    pendingReadRequests.add(requestKey)
    ownsRequest = true

    yield call(
      MessagesService.markMessageAsSeen,
      conversationId,
      payload.messageId
    )
    const readPayload = {
      userId: currentUser.id,
      conversationId,
      messageIds: [payload.messageId],
      seenAt: new Date().toISOString()
    }
    yield put(markMessageAsSeenRoutine.success(readPayload))
    ChatSyncService.getInstance().broadcastMessagesRead(readPayload)
    yield put(getUnreadMessagesCountRoutine.trigger())
  } catch (e) {
    console.log(e)
    yield put(markMessageAsSeenRoutine.failure(payload))
  } finally {
    if (ownsRequest) pendingReadRequests.delete(requestKey)
  }
}

function* getContextAvailableActions({ payload }) {
  yield put(
    getContextAvailableActionsRoutine.request({ chatId: payload?.chatId })
  )
  try {
    const conversationDetails = yield select(state =>
      payload?.chatId
        ? selectChatSessionDetailsByChatId(state, { chatId: payload.chatId })
        : selectConversationDetails(state)
    )
    const conversationId =
      payload?.conversationId || conversationDetails?.conversationId

    if (!conversationId) {
      yield put(
        getContextAvailableActionsRoutine.failure({ chatId: payload?.chatId })
      )
      return
    }

    const { data } = yield call(
      MessagesService.getConversationContextActions,
      conversationId
    )

    //reservation can throw an exception if doesn't exist
    try {
      const response = yield call(
        MessagesService.getConversationContextReservation,
        conversationId
      )
      yield put(
        getContextAvailableActionsRoutine.success({
          chatId: payload?.chatId || conversationDetails.chatId,
          ...data,
          reservationId: response.data.id
        })
      )
    } catch (e) {
      yield put(
        getContextAvailableActionsRoutine.success({
          chatId: payload?.chatId || conversationDetails.chatId,
          ...data
        })
      )
    }
  } catch (e) {
    yield put(
      getContextAvailableActionsRoutine.failure({ chatId: payload?.chatId })
    )
  }
}

function* reserveOffer({ payload }) {
  try {
    switch (payload.contextType) {
      case 'export_cargo_offer':
        yield call(ExchangeService.reserveExportOffer, payload.offerId)
        break
      case 'import_cargo_offer':
        yield call(ExchangeService.reserveImportOffer, payload.offerId)
        break
      case 'general_cargo_offer':
        yield call(ExchangeService.reserveGeneralOffer, payload.offerId)
        break
      case 'post_import_container_offer':
        yield call(ExchangeService.reservePostImportOffer, payload.offerId)
        break
      case 'free_carrier_offer':
        yield call(ExchangeService.reserveFreeCarrierOffer, payload.offerId)
        break
      default:
    }
  } catch (e) {
    console.log(e)
  }
}

function* acceptReservation({ payload }) {
  try {
    switch (payload.contextType) {
      case 'export_cargo_offer':
        yield put(
          acceptExportReservationRoutine({
            offerId: payload.offerId,
            reservationId: payload.reservationId,
            withTracking: payload.withTracking,
            onSuccess: payload.onSuccess
          })
        )
        break
      case 'import_cargo_offer':
        yield put(
          acceptImportReservationRoutine({
            offerId: payload.offerId,
            reservationId: payload.reservationId,
            withTracking: payload.withTracking,
            onSuccess: payload.onSuccess
          })
        )
        break
      case 'general_cargo_offer':
        yield put(
          acceptGeneralReservationRoutine({
            offerId: payload.offerId,
            reservationId: payload.reservationId,
            withTracking: payload.withTracking,
            onSuccess: payload.onSuccess
          })
        )
        break
      case 'post_import_container_offer':
        yield put(
          acceptPostImportReservationRoutine({
            offerId: payload.offerId,
            reservationId: payload.reservationId,
            withTracking: payload.withTracking,
            onSuccess: payload.onSuccess
          })
        )
        break
      case 'free_carrier_offer':
        yield put(
          acceptFreeCarrierReservationRoutine({
            offerId: payload.offerId,
            reservationId: payload.reservationId,
            withTracking: payload.withTracking,
            onSuccess: payload.onSuccess
          })
        )
        break
      default:
    }
  } catch (e) {
    console.log(e)
  }
}

function* rejectReservation({ payload }) {
  try {
    switch (payload.contextType) {
      case 'export_cargo_offer':
        yield call(
          ReservationService.rejectExportReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'import_cargo_offer':
        yield call(
          ReservationService.rejectImportReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'general_cargo_offer':
        yield call(
          ReservationService.rejectGeneralReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'post_import_container_offer':
        yield call(
          ReservationService.rejectPostImportReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'free_carrier_offer':
        yield call(
          ReservationService.rejectFreeCarrierReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      default:
    }
  } catch (e) {
    console.log(e)
  }
}

function* cancelReservation({ payload }) {
  try {
    switch (payload.contextType) {
      case 'export_cargo_offer':
        yield call(
          ReservationService.cancelExportReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'import_cargo_offer':
        yield call(
          ReservationService.cancelImportReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'general_cargo_offer':
        yield call(
          ReservationService.cancelGeneralReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'post_import_container_offer':
        yield call(
          ReservationService.cancelPostImportReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      case 'free_carrier_offer':
        yield call(
          ReservationService.cancelFreeCarrierReservation,
          payload.offerId,
          payload.reservationId
        )
        break
      default:
    }
  } catch (e) {
    console.log(e)
  }
}

function* clearConversation() {
  const isOpened = yield select(isConversationOpened)
  if (isOpened) {
    yield put(clearConversationRoutine.success())
  }
}

function* clearLastMessagesLists() {
  yield put(clearLastMessagesListsRoutine.success())
}

function* getLastMessagesList({ payload = {} }) {
  const refresh = payload.refresh === true
  yield put(getLastContextualMessagesListRoutine.trigger({ page: 1, refresh }))
  yield put(getLastPersonalMessagesListRoutine.trigger({ page: 1, refresh }))
  if (!refresh) yield spawn(getUnreadMessagesCount)
}

function* getLastContextualMessagesList({ payload = {} }) {
  yield put(getLastContextualMessagesListRoutine.request())
  try {
    const currentUser = yield select(selectCurrentUser)
    const messages = yield select(selectMessagesDetails)

    const query = qs.stringify(
      {
        page: payload.page || messages.contextualMessagesPage,
        limit: messages.limit,
        filter: {
          type: 'contextual'
        }
      },
      { skipNull: true }
    )

    if (payload.refresh || !messages.contextualMessagesAllFetched) {
      const response = yield call(MessagesService.getMessagesList, query)

      yield put(
        getLastContextualMessagesListRoutine.success({
          data: normalizeLastMessagesList({
            list: response.data,
            currentUserId: currentUser.id,
            messagesType: 'contextual'
          }),
          meta: response.response.meta,
          append: payload.append,
          refresh: payload.refresh
        })
      )
    }
  } catch (e) {
    console.log(e)
  } finally {
    yield put(getLastContextualMessagesListRoutine.fulfill())
  }
}

function* getLastPersonalMessagesList({ payload = {} }) {
  yield put(getLastPersonalMessagesListRoutine.request())
  try {
    const currentUser = yield select(selectCurrentUser)
    const messages = yield select(selectMessagesDetails)

    const query = qs.stringify(
      {
        page: payload.page || messages.personalMessagesPage,
        limit: messages.limit,
        filter: {
          type: 'personal'
        }
      },
      { skipNull: true }
    )

    if (payload.refresh || !messages.personalMessagesAllFetched) {
      const response = yield call(MessagesService.getMessagesList, query)

      yield put(
        getLastPersonalMessagesListRoutine.success({
          data: normalizeLastMessagesList({
            list: response.data,
            currentUserId: currentUser.id,
            messagesType: 'personal'
          }),
          meta: response.response.meta,
          append: payload.append,
          refresh: payload.refresh
        })
      )
    }
  } catch (e) {
    console.log(e)
  } finally {
    yield put(getLastPersonalMessagesListRoutine.fulfill())
  }
}

function* searchForUserConversations({ payload }) {
  const currentUser = yield select(selectCurrentUser)
  const messages = yield select(selectMessagesDetails)

  yield put(searchForUserConversationsRoutine.request())

  if (
    payload.filter.type === 'contextual' &&
    !messages.contextualSearchResultsAllFetched
  ) {
    const query = qs.stringify(
      {
        ...payload,
        page: payload?.page || messages.contextualSearchResultsPage,
        limit: messages.limit
      },
      { skipNull: true }
    )

    const response = yield call(MessagesService.getMessagesList, query)

    try {
      yield put(
        searchForUserConversationsRoutine.success({
          field: 'contextualSearchResults',
          pageValueName: 'contextualSearchResultsPage',
          allFetchedValueName: 'contextualSearchResultsAllFetched',
          data: normalizeLastMessagesList({
            list: response.response.data,
            currentUserId: currentUser.id,
            messagesType: 'contextual'
          }),
          meta: response.response.meta,
          append: payload.append
        })
      )
    } catch (e) {
      yield put(searchForUserConversationsRoutine.failure())
      console.log(e)
    }
  }

  if (
    payload.filter.type === 'personal' &&
    !messages.personalSearchResultsAllFetched
  ) {
    const query = qs.stringify(
      {
        filter: {
          name: payload.filter.name
        },
        page: payload?.page || messages.personalSearchResultsPage,
        limit: messages.limit
      },
      { skipNull: true }
    )

    const response = yield call(DictionariesService.users, query)

    yield put(
      searchForUserConversationsRoutine.success({
        field: 'personalSearchResults',
        pageValueName: 'personalSearchResultsPage',
        allFetchedValueName: 'personalSearchResultsAllFetched',
        data: normalizeFoundUsers(response.data),
        meta: response.response.meta,
        append: payload.append
      })
    )
  }
}

function listenChatEvents(userId) {
  return eventChannel(emitter => {
    // Skip in SSR environment
    if (typeof window === 'undefined') {
      return () => {}
    }

    LaravelEchoService.getInstance().listenToChatEvents(
      userId,
      (type, payload) => {
        switch (type) {
          case MESSAGE_EVENT_TYPES.MESSAGE_SENT:
            return emitter({
              type: MESSAGE_EVENT_USER_MESSAGE_SENT,
              payload: { ...payload, currentUserId: userId }
            })
          case MESSAGE_EVENT_TYPES.OFFER_RESERVED:
          case MESSAGE_EVENT_TYPES.OFFER_DELETED:
          case MESSAGE_EVENT_TYPES.OFFER_FINISHED:
          case MESSAGE_EVENT_TYPES.OFFER_HANDLER_CHANGED:
          case MESSAGE_EVENT_TYPES.OFFER_BID:
          case MESSAGE_EVENT_TYPES.OFFER_RESERVATION_ACCEPTED:
          case MESSAGE_EVENT_TYPES.OFFER_RESERVATION_CANCELLED:
          case MESSAGE_EVENT_TYPES.OFFER_RESERVATION_QUEUE_PLACE_CHANGED:
          case MESSAGE_EVENT_TYPES.OFFER_RESERVATION_REJECTED:
            return emitter({
              type: MESSAGE_EVENT_SYSTEM_MESSAGE_SENT,
              payload: { ...payload, currentUserId: userId }
            })
          default:
            //handle information about not handled type
            return null
        }
      }
    )
    // unsubscribe function
    return () => {}
  })
}

function listenToChatPresence() {
  return eventChannel(emitter => {
    // Skip in SSR environment
    if (typeof window === 'undefined') {
      return () => {}
    }

    LaravelEchoService.getInstance().listenToChatUserPresence({
      initCallback: users => {
        emitter({
          type: CHAT_PRESENCE_CHANNEL_INIT,
          payload: { users }
        })
      },
      joiningCallback: user => {
        emitter({
          type: CHAT_PRESENCE_CHANNEL_JOIN,
          payload: { user }
        })
      },
      leavingCallback: user => {
        emitter({
          type: CHAT_PRESENCE_CHANNEL_LEAVE,
          payload: { user }
        })
      }
    })

    return () => {}
  })
}

function listenChatBroadcast() {
  return eventChannel(emitter => {
    // Only initialize chat sync service in browser environment
    if (typeof window === 'undefined') {
      // Return empty channel for SSR
      return () => {}
    }

    const chatSyncService = ChatSyncService.getInstance()
    const unsubscribe = chatSyncService.listen(msg => {
      if (!msg) return
      const ownTabId = chatSyncService.broadcastService.tabId
      if (msg?.data?.fromTabId && msg.data.fromTabId === ownTabId) return

      switch (msg.type) {
        case 'USER_MESSAGE_SENT': {
          if (!msg?.data) return
          const messageRecord = normalizeSentMessage(msg.data.message)

          emitter({
            type: CHAT_OTHER_TAB_MESSAGE_SENT,
            payload: {
              userId: msg.data.userId,
              conversationId: msg.data.conversationId,
              message: messageRecord,
              receiverId: msg.data.receiverId,
              contextId: msg.data.contextId,
              contextType: msg.data.contextType
            }
          })
          break
        }
        case 'MESSAGES_READ': {
          if (!msg?.data) return
          emitter({
            type: CHAT_CONVERSATION_MESSAGES_READ,
            payload: {
              userId: msg.data.userId,
              conversationId: msg.data.conversationId,
              messageIds: msg.data.messageIds,
              seenAt: msg.data.seenAt
            }
          })
          break
        }
        default:
          break
      }
    })
    return () => {
      unsubscribe && unsubscribe()
    }
  })
}

function* listenChatBroadcastWatcher() {
  const channel = yield call(listenChatBroadcast)
  while (true) {
    const action = yield take(channel)
    const currentUser = yield select(selectCurrentUser)
    if (
      currentUser?.id &&
      String(action.payload?.userId) === String(currentUser.id)
    ) {
      yield put(action)
      if (action.type === CHAT_CONVERSATION_MESSAGES_READ) {
        yield put(getUnreadMessagesCountRoutine.trigger())
      }
    }
  }
}

export function* getUnreadMessagesCount() {
  try {
    const { data } = yield call(MessagesService.getUnreadMessagesCount)
    yield put(getUnreadMessagesCountRoutine.success(data))
  } catch (e) {
    console.log(e)
  }
}

export function* muteChat() {
  try {
    yield spawn(MessagesService.muteChat)
    yield call(toggleChatNotifications, { payload: true })
  } catch (e) {
    console.log(e)
  }
}

export function* unmuteChat() {
  try {
    yield spawn(MessagesService.unmuteChat)
    yield call(toggleChatNotifications, { payload: false })
  } catch (e) {
    console.log(e)
  }
}

export function* toggleConversationNotifications({ payload }) {
  yield put(toggleConversationNotificationsRoutine.success(payload))
}

export function* muteConversation({ payload }) {
  try {
    const conversationDetails = yield select(state =>
      payload?.chatId
        ? selectChatSessionDetailsByChatId(state, { chatId: payload.chatId })
        : selectConversationDetails(state)
    )
    if (!conversationDetails?.conversationId) return
    yield spawn(
      MessagesService.muteConversation,
      conversationDetails.conversationId
    )
    yield put(
      toggleConversationNotificationsRoutine.success({
        chatId: payload?.chatId || conversationDetails.chatId,
        value: true
      })
    )
  } catch (e) {
    console.log(e)
  }
}

export function* unmuteConversation({ payload }) {
  try {
    const conversationDetails = yield select(state =>
      payload?.chatId
        ? selectChatSessionDetailsByChatId(state, { chatId: payload.chatId })
        : selectConversationDetails(state)
    )
    if (!conversationDetails?.conversationId) return
    yield spawn(
      MessagesService.unmuteConversation,
      conversationDetails.conversationId
    )
    yield put(
      toggleConversationNotificationsRoutine.success({
        chatId: payload?.chatId || conversationDetails.chatId,
        value: false
      })
    )
  } catch (e) {
    console.log(e)
  }
}

export const unlockNotificationSound = async () => {
  try {
    if (!sound) return
    sound.volume = 0
    await sound.play()
    sound.pause()
    sound.currentTime = 0
    sound.volume = 1
    if (typeof window !== 'undefined') {
      localStorage.setItem('chatSoundUnlocked', '1')
    }
  } catch (e) {
    /* noop */
  }
}

const isSoundUnlocked = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      localStorage.getItem('chatSoundUnlocked') === '1'
    )
  } catch (e) {
    return false
  }
}

export const ensureSoundUnlocked = () => {
  if (typeof window === 'undefined') return
  if (isSoundUnlocked()) return
  const handler = async () => {
    await unlockNotificationSound()
    document.removeEventListener('pointerdown', handler, true)
    document.removeEventListener('keydown', handler, true)
    document.removeEventListener('touchstart', handler, true)
  }
  document.addEventListener('pointerdown', handler, true)
  document.addEventListener('keydown', handler, true)
  document.addEventListener('touchstart', handler, true)
}

const playSound = async () => {
  if (typeof window === 'undefined') return
  try {
    let canPlay = true
    try {
      if (navigator?.permissions?.query) {
        const perm = await navigator.permissions.query({
          name: 'notifications'
        })
        canPlay = perm.state === 'granted'
      }
    } catch (e) {
      /* noop */
    }
    if (!canPlay && !isSoundUnlocked()) return
    sound && (await sound.play())
  } catch (e) {
    /* noop */
  }
}

function* receiveMessage({ payload }) {
  try {
    const currentUser = yield select(selectCurrentUser)
    if (
      payload.sender?.id != null &&
      String(payload.sender.id) === String(currentUser.id)
    ) {
      return
    }
    const conversationMuted = payload.is_conversation_muted
    const chatMuted = yield select(isChatMuted)
    const messagesListOpened = yield select(isMessagesListOpened)

    let notificationResult = null
    if (!messagesListOpened && !chatMuted) {
      notificationResult = yield call(showChatMessageNotificationToast, payload)
    }

    // notificationResult ma albo {toastId} albo {desktop: true} w zależności od powiadomienia
    if (
      notificationResult &&
      !conversationMuted &&
      !chatMuted &&
      (notificationResult.toastId || notificationResult.desktop)
    ) {
      yield call(playSound)
    }
  } catch (e) {
    console.log(e)
  }
}

function* refreshOpenMessagesList() {
  yield delay(200)
  const listOpened = yield select(isMessagesListOpened)
  if (listOpened) {
    yield put(getLastMessagesListRoutine.trigger({ refresh: true }))
  }
}

//watchers

export function* listenChatEventsWatcher(userId) {
  //listen to websockets channel
  const channel = yield call(listenChatEvents, userId)
  while (true) {
    const action = yield take(channel)
    yield put(action)
    yield put(getUnreadMessagesCountRoutine.trigger())
  }
}

export function* listenToChatPresenceWatcher() {
  //listen to websockets channel
  const channel = yield call(listenToChatPresence)
  while (true) {
    const action = yield take(channel)
    yield put(action)
  }
}

function* setMessagesListOpenedRoutineWatcher() {
  yield takeLatest(setMessagesListOpenedRoutine.TRIGGER, setMessagesListOpened)
}

function* setConversationOpenedRoutineWatcher() {
  yield takeEvery(setConversationOpenedRoutine.TRIGGER, setConversationOpened)
}

function* sendMessageRoutineWatcher() {
  yield takeEvery(sendMessageRoutine.TRIGGER, sendMessage)
}

function* bootstrapConversationRoutineWatcher() {
  yield takeEvery(bootstrapConversationRoutine.TRIGGER, bootstrapConversation)
}

function* getConversationMessagesRoutineWatcher() {
  yield takeEvery(
    getConversationMessagesRoutine.TRIGGER,
    getConversationMessages
  )
}

function* clearConversationRoutineWatcher() {
  yield takeLatest(clearConversationRoutine.TRIGGER, clearConversation)
}

function* setChatSessionActiveRoutineWatcher() {
  yield takeLatest(setChatSessionActiveRoutine.TRIGGER, setChatSessionActive)
}

function* setChatSessionMinimizedRoutineWatcher() {
  yield takeLatest(
    setChatSessionMinimizedRoutine.TRIGGER,
    setChatSessionMinimized
  )
}

function* closeChatSessionRoutineWatcher() {
  yield takeLatest(closeChatSessionRoutine.TRIGGER, closeChatSession)
}

function* setChatDockConstraintsRoutineWatcher() {
  yield takeLatest(
    setChatDockConstraintsRoutine.TRIGGER,
    setChatDockConstraints
  )
}

function* getLastMessagesListRoutineWatcher() {
  yield takeLatest(getLastMessagesListRoutine.TRIGGER, getLastMessagesList)
}

function* refreshOpenMessagesListWatcher() {
  yield takeLatest(
    [
      sendMessageRoutine.SUCCESS,
      MESSAGE_EVENT_USER_MESSAGE_SENT,
      MESSAGE_EVENT_SYSTEM_MESSAGE_SENT,
      CHAT_OTHER_TAB_MESSAGE_SENT,
      markMessageAsSeenRoutine.SUCCESS,
      CHAT_CONVERSATION_MESSAGES_READ
    ],
    refreshOpenMessagesList
  )
}

function* getUnreadMessagesCountRoutineWatcher() {
  yield takeLatest(getUnreadMessagesCountRoutine.TRIGGER, refreshUnreadCounters)
}

function* refreshUnreadCounters() {
  yield delay(200)
  yield call(getUnreadMessagesCount)
}

function* searchForUserConversationsRoutineWatcher() {
  yield takeLatest(
    searchForUserConversationsRoutine.TRIGGER,
    searchForUserConversations
  )
}

function* getLastContextualMessagesListRoutineWatcher() {
  yield takeLatest(
    getLastContextualMessagesListRoutine.TRIGGER,
    getLastContextualMessagesList
  )
}

function* getLastPersonalMessagesListRoutineWatcher() {
  yield takeLatest(
    getLastPersonalMessagesListRoutine.TRIGGER,
    getLastPersonalMessagesList
  )
}

function* clearLastMessagesListsRoutineWatcher() {
  yield takeLatest(
    clearLastMessagesListsRoutine.TRIGGER,
    clearLastMessagesLists
  )
}

function* muteChatRoutineWatcher() {
  yield takeLatest(muteChatRoutine.TRIGGER, muteChat)
}

function* unmuteChatRoutineWatcher() {
  yield takeLatest(unmuteChatRoutine.TRIGGER, unmuteChat)
}

function* toggleConversationNotificationsRoutineWatcher() {
  yield takeLatest(
    toggleConversationNotificationsRoutine.TRIGGER,
    toggleConversationNotifications
  )
}

function* muteConversationRoutineWatcher() {
  yield takeEvery(muteConversationRoutine.TRIGGER, muteConversation)
}

function* unmuteConversationRoutineWatcher() {
  yield takeEvery(unmuteConversationRoutine.TRIGGER, unmuteConversation)
}

function* getConversationContextRoutineWatcher() {
  yield takeEvery(getConversationContextRoutine.TRIGGER, getConversationContext)
}

function* markConversationAsSeenRoutineWatcher() {
  yield takeEvery(markConversationAsSeenRoutine.TRIGGER, markConversationAsSeen)
}

function* markMessageAsSeenRoutineWatcher() {
  yield takeEvery(markMessageAsSeenRoutine.TRIGGER, markMessageAsSeen)
}

function* reserveOfferRoutineWatcher() {
  yield takeLatest(reserveOfferRoutine.TRIGGER, reserveOffer)
}

function* getContextAvailableActionsRoutineWatcher() {
  yield takeEvery(
    getContextAvailableActionsRoutine.TRIGGER,
    getContextAvailableActions
  )
}

function* acceptReservationRoutineWatcher() {
  yield takeLatest(acceptReservationRoutine.TRIGGER, acceptReservation)
}

function* rejectReservationRoutineWatcher() {
  yield takeLatest(rejectReservationRoutine.TRIGGER, rejectReservation)
}

function* cancelReservationRoutineWatcher() {
  yield takeLatest(cancelReservationRoutine.TRIGGER, cancelReservation)
}

function* receiveMessageRoutineWatcher() {
  yield takeEvery(MESSAGE_EVENT_USER_MESSAGE_SENT, receiveMessage)
}

export const messagesSagas = [
  fork(setMessagesListOpenedRoutineWatcher),
  fork(setConversationOpenedRoutineWatcher),
  fork(sendMessageRoutineWatcher),
  fork(bootstrapConversationRoutineWatcher),
  fork(getConversationMessagesRoutineWatcher),
  fork(clearConversationRoutineWatcher),
  fork(setChatSessionActiveRoutineWatcher),
  fork(setChatSessionMinimizedRoutineWatcher),
  fork(closeChatSessionRoutineWatcher),
  fork(setChatDockConstraintsRoutineWatcher),
  fork(getLastMessagesListRoutineWatcher),
  fork(refreshOpenMessagesListWatcher),
  fork(getUnreadMessagesCountRoutineWatcher),
  fork(searchForUserConversationsRoutineWatcher),
  fork(getLastContextualMessagesListRoutineWatcher),
  fork(getLastPersonalMessagesListRoutineWatcher),
  fork(clearLastMessagesListsRoutineWatcher),
  fork(muteChatRoutineWatcher),
  fork(unmuteChatRoutineWatcher),
  fork(toggleConversationNotificationsRoutineWatcher),
  fork(muteConversationRoutineWatcher),
  fork(unmuteConversationRoutineWatcher),
  fork(getConversationContextRoutineWatcher),
  fork(markConversationAsSeenRoutineWatcher),
  fork(markMessageAsSeenRoutineWatcher),
  fork(reserveOfferRoutineWatcher),
  fork(getContextAvailableActionsRoutineWatcher),
  fork(acceptReservationRoutineWatcher),
  fork(rejectReservationRoutineWatcher),
  fork(cancelReservationRoutineWatcher),
  fork(receiveMessageRoutineWatcher),
  fork(listenChatBroadcastWatcher)
]
